import {observer} from 'mobx-react-lite';
import * as React from 'react';
import {FeatureAppSectionModel} from '../../../../generated/core';
import {AuthoringWrapper} from '../../../components/AuthoringWrapper';
import {LegalEntityContextData} from '../../../context/legal/LegalEntityProvider';
import {MapTo} from '../../../infrastructure/compatibility/MapTo';
import {C} from '../../../registries/compatibilty';
import {createFeatureAppSectionTrackingData} from '../../../utils/tracking/createFeatureAppSectionTrackingData';

import {useIntersectionTrigger} from './section-wrapper-utils/intersectionTrigger';
import {SectionWrapper} from './SectionWrapper';
import {FeatureAppContent} from './FeatureAppContent';
import {useSectionVisibility} from '../../../infrastructure/compatibility/useSectionVisibility';
import {FeatureApp} from '../../structure/FeatureApp';

const EMPTY_LABEL = 'S122 Feature App Section';

export function useViewportCheck() {
    const [sectionRefInitialized, setSectionRefInitialized] =
        React.useState(false);
    const sectionWrapperRef = React.useRef<HTMLDivElement | null>(null);
    const refCallback = React.useCallback(
        (callBackRef: React.RefObject<HTMLDivElement>) => {
            if (
                callBackRef.current &&
                callBackRef.current !== sectionWrapperRef.current
            ) {
                sectionWrapperRef.current = callBackRef.current;
                setSectionRefInitialized(true);
            }
        },
        [sectionWrapperRef]
    );

    const [loadFA, setLoadFA] = React.useState(false);
    useIntersectionTrigger(
        sectionWrapperRef,
        (_entries: IntersectionObserverEntry[]) => {
            if (!Boolean(loadFA)) {
                setLoadFA(true);
            }
        },
        (entry: IntersectionObserverEntry) => {
            return entry.isIntersecting;
        },
        0,
        0.01,
        sectionRefInitialized
    );

    return {
        setElementRef: refCallback,
        elementInViewport: loadFA
    };
}

const InternalFeatureAppSection = observer(function ObservedFeatureAppSection(
    props: FeatureAppSectionModel & LegalEntityContextData
): JSX.Element | null {
    const {elementInViewport, setElementRef} = useViewportCheck();
    const {setVisible} = useSectionVisibility();
    const {
        anchorId,
        config,
        contentId: sectionId,
        contentLabels,
        contentName,
        tagTypeBroken,
        mboxId
    } = props;

    const sectionTrackingData = createFeatureAppSectionTrackingData(config);

    return (
        <AuthoringWrapper
            anchorId={anchorId}
            title={EMPTY_LABEL}
            bgColor={AuthoringWrapper.BG_COLOR_SECTION}
            tagTypeBroken={tagTypeBroken}
        >
            {C.isInEditor() && config && (
                <FeatureAppContent path="content" name={config.name} />
            )}

            <SectionWrapper
                anchorId={anchorId}
                sectionId={sectionId}
                contentLabels={contentLabels}
                additionalTrackingData={sectionTrackingData}
                contentName={contentName}
                customDataAttribute={{
                    key: 'data-component',
                    value: 'feature-app-section'
                }}
                refCallback={setElementRef}
                mboxId={mboxId}
            >
                <FeatureApp
                    {...props}
                    elementInViewport={elementInViewport}
                    onHasContentChanged={setVisible}
                />
            </SectionWrapper>
        </AuthoringWrapper>
    );
});

export const FA_SECTION_RESOURCETYPE =
    'vwa-ngw18/components/editorial/sections/featureAppSection';
export const FeatureAppSection = MapTo(
    FA_SECTION_RESOURCETYPE,
    InternalFeatureAppSection
);
